/**
 * Where the v2 editor's collaboration socket lives — the ONE reader of `VITE_COLLAB_URL`.
 *
 * Default: **same origin**, `wss://<this host>/collaboration`, because desk is a single-origin
 * app and hard-coding a second host into the bundle is how "desk is same-origin" quietly stops
 * being true. The env var exists for one situation only: a host whose reverse proxy does not
 * yet route `/collaboration`, where pointing at one that does unblocks a build.
 *
 * ⚠ As of 2026-09-09 `desk.docit.pro`'s Caddy vhost serves `/api/*`, `/connect/*`, `/healthz`
 * and static files, and has NO `/collaboration` handle (nor `/hubs/*`). Until the handle lands
 * — `waves/W6-caddy-desk-handles.snippet`, copied from the `kms.docit.pro` vhost, HTTP/1.1
 * transport MANDATORY because a WebSocket upgrade over h2 fails — the socket is refused here.
 * That is exactly why `EditorV2Host` renders a connection state instead of assuming one: an
 * unrouted endpoint makes a Hocuspocus provider retry forever, silently, with no error thrown.
 */

const CONFIGURED = import.meta.env.VITE_COLLAB_URL?.trim();

/**
 * A RUNTIME override, set once at boot by a host that is not a desk browser tab — the VS Code
 * "Docit" panel (thinkinghub-vscode/webviews/docit-pages). Its page origin is `vscode-webview://…`,
 * so "same origin" means nothing there: it names the real Docit socket origin, and it supplies the
 * token as a FUNCTION, because the panel's token is short-lived and refreshed by the extension host —
 * a function lets a reconnect pick up the fresh token without remounting the editor.
 */
export interface CollaborationOverride {
  url: string;
  token: () => string;
}

let override: CollaborationOverride | null = null;

/** Install (or with `null`, remove) the runtime override. */
export function configureCollaboration(next: CollaborationOverride | null): void {
  override = next;
}

/** The runtime token source, when a host installed one; `undefined` in a desk browser tab. */
export function collaborationTokenSource(): (() => string) | undefined {
  return override?.token;
}

/**
 * `wss://…/collaboration` for this deployment. Falls back to `ws:` on a plain-HTTP origin so
 * `pnpm dev` works; behind Caddy the page is always https, so this is always `wss:` in
 * production.
 */
export function collaborationUrl(): string {
  if (override) {
    return override.url.replace(/\/$/, "");
  }
  if (CONFIGURED) {
    return CONFIGURED.replace(/\/$/, "");
  }
  if (typeof window === "undefined") {
    return "/collaboration";
  }
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${window.location.host}/collaboration`;
}

/** True when the endpoint was pointed at a different origin than the page. */
export function isCrossOriginCollab(): boolean {
  if (override) {
    return true;
  }
  if (!CONFIGURED || typeof window === "undefined") {
    return false;
  }
  try {
    return new URL(CONFIGURED).host !== window.location.host;
  } catch {
    return false;
  }
}
